import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {
  ActivityIndicator,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native'
import Svg, {Line, Path, Rect} from 'react-native-svg'
import {Feather} from '@expo/vector-icons'
import {useLingui} from '@lingui/react/macro'

import {playReadingAudio, stopReadingAudio} from '#/lib/flashbookAudio'
import {
  AUDIO_PRESETS,
  type AudioBufferLike,
  audioContainerLabel,
  type AudioEditSettings,
  type AudioOpenError,
  type AudioPresetId,
  audioProblemOf,
  type AudioTrim,
  canEditAudio,
  clamp,
  computePeaks,
  decodeAudioForEditing,
  describeSettings,
  detectSilenceTrim,
  editKey,
  FLAT_SETTINGS,
  formatDuration,
  getAudioPreset,
  isMeaningfulTrim,
  isWebKitHostileContainer,
  matchAudioPreset,
  renderedClipUrl,
  renderEditedAudio,
} from '#/lib/flashbookAudioEditor'
import {getAudioEngine} from '#/lib/flashbookAudioEngine'
import {type AutoMasterResult, masterTake} from '#/lib/flashbookAutoMaster'
import {clipSourceFor} from '#/lib/flashbookClips'
import {formatGain, formatLufs} from '#/lib/flashbookLoudness'
import * as Toast from '#/view/com/util/Toast'
import {readableInk, type StudioChrome} from '#/themes-studio/chrome'
import {useStudioChrome, useStudioStyles} from '#/themes-studio/useChrome'

type Props = {
  /** The clip being edited — a recording or an uploaded file. */
  url: string
  accent: string
  /** Hands the edited clip back to the field (a fresh, playable URL). */
  onApply: (url: string) => void
  onClose: () => void
}

const WAVE_H = 64

/**
 * The reading-audio mastering desk.
 *
 * It exists because a creator records a page on a phone, in a room, and the
 * result is thin, roomy and quiet next to the rest of the book. None of that is a
 * taste question, so the desk's front door is **Auto master** — one tap that
 * listens to the take, cleans the room, brings the voice forward and levels the
 * page to the book's own target, and then *says what it did*. Under it sits the
 * one manual thing a creator can hold an opinion about: **Voice effects** —
 * `Natural`, `Warm`, `Clear`, `Storyteller` and the rest.
 *
 * That is the whole desk, and the narrowness is the design. It used to be a
 * console: five technical dials (`Clean up`, `Warmth`, `Clarity`, `Evenness`,
 * `Room`), a row of fade lengths and a `Fine-tune` disclosure to hide them
 * behind. Every one of those needed its purpose explained before it could be
 * used, and a creator recording a page of a textbook is not mixing an album —
 * they are choosing how they should sound. The dials are gone; the engine behind
 * them is not, so a preset still sets the same clearing, warmth, room and pace it
 * always did, and auto master still sets all of it from a measurement.
 *
 * Every preview is an actual offline render of exactly what "Apply" would
 * write, so nothing here can sound better in the editor than in the book.
 */
export function FlashBookAudioEditor({url, accent, onApply, onClose}: Props) {
  const styles = useStyles()
  const chrome = useStudioChrome()
  const accentInk = readableInk(accent).ink
  const {t: l} = useLingui()
  /** The decode result, tagged with the URL it came from. */
  const [decoded, setDecoded] = useState<{
    url: string
    buffer: AudioBufferLike
  } | null>(null)
  /** Why the clip could not be opened, if it could not be. */
  const [failure, setFailure] = useState<AudioOpenError | null>(null)
  /** Bumped by "Try again", so a clip that failed for a passing reason retries. */
  const [attempt, setAttempt] = useState(0)
  // Derived rather than reset inside the effect: when the URL changes the old
  // decode simply stops matching, so there is no stale-frame flash to suppress.
  const source = decoded && decoded.url === url ? decoded.buffer : null
  const [settings, setSettings] = useState<AudioEditSettings>(
    () => getAudioPreset('natural').settings,
  )
  const [trim, setTrim] = useState<AudioTrim>({start: 0, end: 1})
  const [busy, setBusy] = useState(false)
  const [playing, setPlaying] = useState<'none' | 'edited' | 'original'>('none')
  const [progress, setProgress] = useState(0)
  const [width, setWidth] = useState(0)
  /**
   * Auto master renders and measures, which takes a moment — so it gets its own
   * flag rather than borrowing `busy`, which drives the transport's spinner.
   */
  const [mastering, setMastering] = useState(false)
  /**
   * What auto master did, tagged with the edit it did it to.
   *
   * Tagged rather than cleared, because that is what keeps the report honest for
   * free: choose a voice effect afterwards and the key no longer matches, so the
   * card stops claiming the take sounds like the report says it does. No wiring
   * to remember and no stale sentence about a take that has since changed.
   */
  const [mastered, setMastered] = useState<{
    key: string
    result: AutoMasterResult
  } | null>(null)
  /** The panel itself, scrolled into view as it opens. */
  const panelRef = useRef<View>(null)

  const activePreset = useMemo(() => matchAudioPreset(settings), [settings])
  /** The edit the desk is currently showing — auto master's report is keyed to it. */
  const currentKey = useMemo(() => editKey(settings, trim), [settings, trim])
  const masterResult =
    mastered && mastered.key === currentKey ? mastered.result : null

  // Rendered previews, keyed by the exact edit they belong to. Switching presets
  // back and forth then costs nothing.
  const renderCache = useRef(new Map<string, string>())
  const handleRef = useRef<ReturnType<typeof playReadingAudio> | null>(null)

  const peaks = useMemo(
    () => (source ? computePeaks(source, 180) : []),
    [source],
  )

  // ── Decode once, then keep it ───────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false
    setFailure(null)
    decodeAudioForEditing(url)
      .then(buffer => {
        if (!cancelled) setDecoded({url, buffer})
      })
      .catch(error => {
        // Swallowed before, which is why a clip that never downloaded was
        // reported as a clip the browser could not decode.
        if (!cancelled) setFailure(audioProblemOf(error))
      })
    return () => {
      cancelled = true
    }
  }, [attempt, url])

  /**
   * What the creator is told when a clip cannot be opened.
   *
   * One sentence — *"the browser could not decode it"* — used to cover every
   * failure there is, so a take that never downloaded read as a corrupt file and
   * a WebM take on an iPhone read as a mystery. Each kind of failure now says
   * what happened, and the two that can be acted on say what to do instead.
   */
  const problemMessage = useMemo(() => {
    if (!failure) return ''
    switch (failure.problem) {
      case 'no-engine':
        return l`This device has no audio engine to edit with.`
      case 'unreadable':
        return l`This clip could not be loaded, so there is nothing to edit. Check the connection, then try again.`
      case 'too-large':
        return l`This clip is too long to edit here — it would not fit in memory. Record a shorter page, or split the reading.`
      default: {
        const label = audioContainerLabel(failure.container) || l`unrecognised`
        /*
         * The advice is ordered by what actually fixes it. A WebM or OGG take —
         * what a browser records by default — cannot be read by WebKit at all,
         * so re-recording on the device at hand is the shortest road back. Any
         * other container simply has no decoder here, and a take the creator
         * already owns is worth more than another performance.
         */
        return isWebKitHostileContainer(failure.container)
          ? l`This take is ${label}, which cannot be opened for editing. Record the reading again on this device — it saves an M4A — or upload a WAV, MP3 or M4A take.`
          : l`This clip is ${label}, which this browser cannot open for editing. Upload a WAV, MP3 or M4A take instead.`
      }
    }
  }, [failure, l])

  // Stop on close so a preview cannot outlive the desk.
  useEffect(() => {
    return () => {
      handleRef.current?.stop()
      handleRef.current = null
      stopReadingAudio()
    }
  }, [])

  /**
   * Bring the desk into view as it opens.
   *
   * It renders where the block's audio band was, which is *under* three text
   * fields, inside a card whose editor region scrolls. On a phone that made the
   * Edit button look broken: the desk opened below the fold, so tapping it
   * appeared to do nothing at all. Web-only by construction — the desk itself
   * only exists where Web Audio does — and a frame is waited for, because the
   * desk has to have laid out before there is anything worth scrolling to.
   */
  useEffect(() => {
    const id = requestAnimationFrame?.(() => {
      const node = panelRef.current as unknown as {
        scrollIntoView?: (options?: ScrollIntoViewOptions) => void
      } | null
      node?.scrollIntoView?.({block: 'start'})
    })
    return () => {
      if (id !== undefined) cancelAnimationFrame?.(id)
    }
  }, [])

  const applyPreset = useCallback((id: AudioPresetId) => {
    setSettings(getAudioPreset(id).settings)
  }, [])

  const trimmedDuration = useMemo(() => {
    if (!source) return 0
    const speed = clamp(settings.speed, 0.5, 2)
    return ((source.duration || 0) * (trim.end - trim.start)) / speed
  }, [settings.speed, source, trim.end, trim.start])

  /** Renders the current edit, reusing the cache when the key is unchanged. */
  const renderCurrent = useCallback(async (): Promise<string | null> => {
    if (!source) return null
    const key = editKey(settings, trim)
    const cached = renderCache.current.get(key)
    if (cached) return cached
    const rendered = await renderEditedAudio(source, settings, trim)
    const clipUrl = await renderedClipUrl(rendered, key)
    renderCache.current.set(key, clipUrl)
    // A long editing session must not leak every intermediate render: a blob URL
    // on the web, a file in the cache directory on a phone.
    if (renderCache.current.size > 12) {
      const oldest = renderCache.current.keys().next().value
      if (oldest && oldest !== key) {
        const stale = renderCache.current.get(oldest)
        renderCache.current.delete(oldest)
        if (stale) getAudioEngine().releaseClip(stale)
      }
    }
    return clipUrl
  }, [settings, source, trim])

  const stopPlayback = useCallback(() => {
    handleRef.current?.stop()
    handleRef.current = null
    setPlaying('none')
    setProgress(0)
  }, [])

  const previewEdited = useCallback(async () => {
    setBusy(true)
    try {
      const objectUrl = await renderCurrent()
      if (!objectUrl) return
      handleRef.current?.stop()
      setPlaying('edited')
      setProgress(0)
      handleRef.current = playReadingAudio(objectUrl, {
        onEnded: () => {
          setPlaying('none')
          setProgress(0)
        },
        onError: () => {
          setPlaying('none')
          Toast.show(l`Could not play that preview.`, 'circle-exclamation')
        },
      })
    } catch {
      Toast.show(l`Could not process that clip.`, 'circle-exclamation')
    } finally {
      setBusy(false)
    }
  }, [l, renderCurrent])

  const previewOriginal = useCallback(() => {
    handleRef.current?.stop()
    setPlaying('original')
    setProgress(0)
    // The clip's bytes when this device holds them, its asset URL otherwise — the
    // comparison is the same either way, one of them without a download.
    handleRef.current = playReadingAudio(clipSourceFor(url), {
      onEnded: () => {
        setPlaying('none')
        setProgress(0)
      },
      onError: () => setPlaying('none'),
    })
  }, [url])

  // A moving playhead over the waveform: reading along to your own voice is how
  // you spot the passage that drags.
  useEffect(() => {
    if (playing === 'none') return
    const id = setInterval(() => {
      const h = handleRef.current
      if (!h) return
      const p = h.getProgress()
      if (p.duration > 0) {
        setProgress(clamp(p.position / p.duration, 0, 1))
      }
    }, 140)
    return () => clearInterval(id)
  }, [playing])

  const onTrimSilence = useCallback(() => {
    if (!source) return
    const found = detectSilenceTrim(source)
    if (!isMeaningfulTrim(found)) {
      Toast.show(l`There is no silence to trim.`, 'info')
      return
    }
    setTrim(found)
  }, [l, source])

  /**
   * One tap: hear the take, clean it, shape it, level it, and say what happened.
   *
   * The result lands in the desk's own state, so the voice-effect chips answer to
   * it exactly as they would to a press. That matters more than it looks: it means
   * the one-tap action is a *starting point* rather than a black box, and a
   * creator who disagrees can choose a voice instead and change the sound without
   * losing anything else the master did.
   */
  const autoMaster = useCallback(async () => {
    if (!source) return
    setMastering(true)
    try {
      const result = await masterTake(source)
      setSettings(result.settings)
      setTrim(result.trim)
      setMastered({key: editKey(result.settings, result.trim), result})
    } catch {
      Toast.show(l`Could not master that clip.`, 'circle-exclamation')
    } finally {
      setMastering(false)
    }
  }, [l, source])

  /** The card's one sentence: its promise before, its report after. */
  const masterNote = useMemo(() => {
    if (!masterResult) {
      return l`Listens to the take, then cleans the room, brings the voice forward and levels the page — nothing to set by hand.`
    }
    // Nothing measured is nothing done. Saying "cleaned the room" about a clip of
    // digital silence would be the desk inventing work it did not do.
    if (masterResult.reading.loudness.lufs === null) {
      return l`There is no sound in this take to master.`
    }
    const parts: string[] = []
    if (masterResult.reading.trimmedSeconds > 0.05) {
      /*
       * One decimal, not `formatDuration`'s `m:ss`. The dead air auto master
       * removes is usually a fraction of a second, and "Trimmed 0:00 of
       * silence" is a sentence that reads as a bug.
       */
      parts.push(
        l`Trimmed ${masterResult.reading.trimmedSeconds.toFixed(1)} s of silence`,
      )
    }
    parts.push(l`Cleaned the room`)
    if (masterResult.reading.dynamicsDb >= 12)
      parts.push(l`Evened out the level`)
    if (masterResult.settings.warmth > 0.05) parts.push(l`Added body`)
    if (masterResult.settings.clarity > 0.05)
      parts.push(l`Brought the words forward`)
    return parts.join(' · ')
  }, [l, masterResult])

  /**
   * Where the page landed.
   *
   * The one line of auto-mastering a creator can check against the rest of their
   * book, so it is a *measured* figure and not the plan's prediction — the level
   * was decided from a real render, so it can be reported from one. With no engine
   * to render with, the desk says which cruder rule it used rather than naming a
   * loudness it never heard.
   */
  const masterLevel = useMemo(() => {
    if (!masterResult) return null
    if (masterResult.measured && masterResult.lufs !== null) {
      return l`Levelled ${formatGain(masterResult.gainDb)} to ${formatLufs(masterResult.lufs)}`
    }
    if (Math.abs(masterResult.gainDb) < 0.1) return null
    return l`Levelled ${formatGain(masterResult.gainDb)} to the take's loudest moment`
  }, [l, masterResult])

  const apply = useCallback(async () => {
    setBusy(true)
    try {
      const objectUrl = await renderCurrent()
      if (!objectUrl) throw new Error('nothing rendered')
      stopPlayback()
      onApply(objectUrl)
    } catch {
      Toast.show(l`Could not apply those effects.`, 'circle-exclamation')
    } finally {
      setBusy(false)
    }
  }, [l, onApply, renderCurrent, stopPlayback])

  const reset = useCallback(() => {
    setSettings(FLAT_SETTINGS)
    setTrim({start: 0, end: 1})
  }, [])

  // ── Waveform geometry ───────────────────────────────────────────────────────
  const mid = WAVE_H / 2
  const wavePath = useMemo(() => {
    if (peaks.length === 0 || width <= 0) return ''
    const step = width / peaks.length
    const scale = WAVE_H * 0.46
    const top: string[] = []
    const bottom: string[] = []
    peaks.forEach((p, i) => {
      const x = (i * step + step / 2).toFixed(1)
      const h = Math.max(0.5, p * scale)
      top.push(`${i === 0 ? 'M' : 'L'}${x},${(mid - h).toFixed(1)}`)
      bottom.push(`L${x},${(mid + h).toFixed(1)}`)
    })
    return `${top.join(' ')} ${bottom.reverse().join(' ')} Z`
  }, [mid, peaks, width])

  const trimStartX = width * trim.start
  const trimEndX = width * trim.end
  const playheadX = width * progress

  return (
    <View
      ref={panelRef}
      style={[styles.panel, {borderColor: accent + '44'}]}
      testID="flashbook-audio-editor">
      <View style={styles.header}>
        <Feather name="sliders" size={12} color={accent} />
        <Text style={[styles.title, {color: accent}]}>{l`AUDIO EDITOR`}</Text>
        <Text style={styles.subtitle} numberOfLines={1}>
          {describeSettings(settings)}
        </Text>
        {/*
          Reset lives in the header rather than at the foot of the desk.

          It is the *undo everything* action and it used to be the last row of a
          desk that is 700px tall on a phone, which made the panel read as one
          control longer than it is. Up here it costs no height at all, and it is
          still far from Apply — the one control that changes the block.
        */}
        {source && (
          <TouchableOpacity
            accessibilityRole="button"
            accessibilityLabel={l`Reset every effect`}
            accessibilityHint={l`Puts the take back to the recording you made`}
            onPress={reset}
            hitSlop={{top: 8, bottom: 8, left: 8, right: 8}}
            style={styles.iconBtn}>
            <Feather name="refresh-ccw" size={13} color={chrome.textMuted} />
          </TouchableOpacity>
        )}
        <TouchableOpacity
          accessibilityRole="button"
          accessibilityLabel={l`Close the audio editor`}
          accessibilityHint={l`Returns to the block without changing your audio`}
          onPress={() => {
            stopPlayback()
            onClose()
          }}
          style={styles.iconBtn}>
          <Feather name="x" size={14} color={chrome.textMuted} />
        </TouchableOpacity>
      </View>

      {/*
        Auto master — the desk's front door, and the first thing on it.

        It sits above the transport on purpose. The loop a creator actually runs
        is *fix this take, hear it, keep it*, so the fix comes first and the only
        thing that answers it by hand — the voice effects — waits below the
        waveform, where a creator reaches once they have heard what the master
        did. The card is a sentence and one button, which is also why the report
        replaces the sentence rather than appearing beside it: there is only ever
        one thing to say about this take.
      */}
      {!failure && source && (
        <View
          style={[
            styles.master,
            {borderColor: masterResult ? accent + '55' : chrome.borderStrong},
          ]}
          testID="flashbook-auto-master">
          <View style={styles.masterHead}>
            <View style={[styles.masterIcon, {backgroundColor: accent + '1A'}]}>
              <Feather name="zap" size={12} color={accent} />
            </View>
            <Text style={[styles.masterTitle, {color: chrome.text}]}>
              {l`Auto master`}
            </Text>
            <TouchableOpacity
              accessibilityRole="button"
              accessibilityLabel={
                masterResult ? l`Master this take again` : l`Master this take`
              }
              accessibilityHint={l`Cleans the room, shapes the voice and levels the page in one go`}
              onPress={() => void autoMaster()}
              disabled={mastering}
              // The desk's smallest control and its most important one, so its
              // touch target is grown without growing the button.
              hitSlop={{top: 8, bottom: 8, left: 4, right: 4}}
              testID="flashbook-auto-master-go"
              style={[styles.masterBtn, {backgroundColor: accent}]}>
              {mastering ? (
                <ActivityIndicator size="small" color={accentInk} />
              ) : (
                <Feather name="zap" size={12} color={accentInk} />
              )}
              <Text style={[styles.masterBtnTxt, {color: accentInk}]}>
                {mastering
                  ? l`Listening…`
                  : masterResult
                    ? l`Again`
                    : l`Master`}
              </Text>
            </TouchableOpacity>
          </View>
          <Text style={styles.masterTxt} testID="flashbook-auto-master-note">
            {masterNote}
          </Text>
          {masterLevel && (
            <Text
              style={[styles.masterMeta, {color: accent}]}
              testID="flashbook-auto-master-level">
              {masterLevel}
            </Text>
          )}
        </View>
      )}

      {failure ? (
        <View
          style={[
            styles.notice,
            {
              borderColor: chrome.warningBorder,
              backgroundColor: chrome.warningTint,
            },
          ]}
          testID="flashbook-audio-problem">
          <View style={styles.noticeRow}>
            <Feather name="alert-circle" size={13} color={chrome.warning} />
            <Text style={styles.noticeTxt}>{problemMessage}</Text>
          </View>
          {failure.problem !== 'no-engine' && (
            <TouchableOpacity
              accessibilityRole="button"
              accessibilityLabel={l`Try opening the clip again`}
              accessibilityHint={l`Reads the audio once more`}
              onPress={() => setAttempt(n => n + 1)}
              hitSlop={{top: 8, bottom: 8, left: 8, right: 8}}
              style={[styles.miniBtn, {borderColor: chrome.warning}]}>
              <Feather name="refresh-cw" size={11} color={chrome.warning} />
              <Text style={[styles.miniTxt, {color: chrome.warning}]}>
                {l`Try again`}
              </Text>
            </TouchableOpacity>
          )}
        </View>
      ) : !source ? (
        <View style={styles.loading}>
          <ActivityIndicator size="small" color={accent} />
          <Text style={styles.loadingTxt}>{l`Reading the waveform…`}</Text>
        </View>
      ) : (
        <>
          {/*
            The desk bar — first, and never below the fold.

            A phone has ~330px of card width and the desk is ~700px tall, so the
            two things a creator actually came for — *hear it*, *keep it* — used
            to sit at the very bottom of a scrolling card. Everything else in the
            desk may scroll; this bar does not leave the screen. It is also where
            an editor is conventionally laid out: transport above the waveform,
            the sound underneath as the detail.
          */}
          <View style={styles.transport}>
            <TouchableOpacity
              accessibilityRole="button"
              accessibilityLabel={
                playing === 'edited' ? l`Stop` : l`Preview with effects`
              }
              accessibilityHint={l`Hears the clip exactly as it will be saved`}
              onPress={() =>
                playing === 'edited' ? stopPlayback() : void previewEdited()
              }
              disabled={busy}
              style={[styles.playBtn, {backgroundColor: accent}]}>
              {busy ? (
                <ActivityIndicator size="small" color={accentInk} />
              ) : (
                <Feather
                  name={playing === 'edited' ? 'pause' : 'play'}
                  size={15}
                  color={accentInk}
                />
              )}
              <Text style={[styles.playTxt, {color: accentInk}]}>
                {playing === 'edited' ? l`Stop` : l`Preview`}
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              accessibilityRole="button"
              accessibilityLabel={
                playing === 'original' ? l`Stop` : l`Compare with the original`
              }
              accessibilityHint={l`Plays the unedited take, so the two can be compared`}
              onPress={() =>
                playing === 'original' ? stopPlayback() : previewOriginal()
              }
              style={[styles.btn, {borderColor: chrome.borderStrong}]}>
              <Feather
                name={playing === 'original' ? 'square' : 'rotate-ccw'}
                size={12}
                color={chrome.textMuted}
              />
              <Text
                style={[
                  styles.btnTxt,
                  {color: chrome.textMuted},
                ]}>{l`Original`}</Text>
            </TouchableOpacity>

            <View style={styles.transportSpacer} />

            <TouchableOpacity
              accessibilityRole="button"
              accessibilityLabel={l`Keep the edited audio`}
              accessibilityHint={l`Replaces this block's audio with the edited clip`}
              onPress={() => void apply()}
              disabled={busy}
              style={[styles.playBtn, {backgroundColor: accent}]}>
              {busy ? (
                <ActivityIndicator size="small" color={accentInk} />
              ) : (
                <Feather name="check" size={14} color={accentInk} />
              )}
              <Text
                style={[styles.playTxt, {color: accentInk}]}>{l`Apply`}</Text>
            </TouchableOpacity>
          </View>

          {/* The waveform *is* the trim control: what is dimmed will not be kept. */}
          <View
            style={styles.waveWrap}
            testID="flashbook-audio-waveform"
            onLayout={e => setWidth(e.nativeEvent.layout.width)}>
            <Svg width="100%" height={WAVE_H}>
              <Rect
                x={0}
                y={0}
                width={width}
                height={WAVE_H}
                fill={accent + '10'}
              />
              {/*
                The trimmed-away ends are scrimmed, and the scrim comes from the
                appearance: the literal this used to carry was a 66% near-black
                that read as a black bar across a light studio, and barely at all
                on a dark one.
              */}
              {trimStartX > 0 && (
                <Rect
                  x={0}
                  y={0}
                  width={trimStartX}
                  height={WAVE_H}
                  fill={chrome.scrim}
                />
              )}
              {trimEndX < width && (
                <Rect
                  x={trimEndX}
                  y={0}
                  width={Math.max(0, width - trimEndX)}
                  height={WAVE_H}
                  fill={chrome.scrim}
                />
              )}
              <Path d={wavePath} fill={accent} />
              <Line
                x1={0}
                y1={mid}
                x2={width}
                y2={mid}
                stroke={chrome.border}
                strokeWidth={1}
              />
              {playing !== 'none' && (
                <Line
                  x1={playheadX}
                  y1={0}
                  x2={playheadX}
                  y2={WAVE_H}
                  stroke={chrome.text}
                  strokeWidth={1.5}
                />
              )}
            </Svg>
            <View style={styles.trimRow}>
              <Text style={styles.trimTxt}>
                {l`Keeping`}{' '}
                <Text style={[styles.trimStrong, {color: accent}]}>
                  {formatDuration(trimmedDuration)}
                </Text>
              </Text>
              <TouchableOpacity
                accessibilityRole="button"
                accessibilityLabel={l`Trim the silence`}
                accessibilityHint={l`Cuts the quiet part before and after your voice`}
                onPress={onTrimSilence}
                hitSlop={{top: 6, bottom: 6, left: 6, right: 6}}
                style={[styles.miniBtn, {borderColor: accent + '55'}]}>
                <Feather name="scissors" size={11} color={accent} />
                <Text style={[styles.miniTxt, {color: accent}]}>{l`Trim`}</Text>
              </TouchableOpacity>
              {isMeaningfulTrim(trim) && (
                <TouchableOpacity
                  accessibilityRole="button"
                  accessibilityLabel={l`Undo the trim`}
                  accessibilityHint={l`Puts the whole clip back`}
                  onPress={() => setTrim({start: 0, end: 1})}
                  style={styles.miniGhost}>
                  <Feather
                    name="rotate-ccw"
                    size={11}
                    color={chrome.textFaint}
                  />
                </TouchableOpacity>
              )}
              {/*
                `Normalise` used to sit here, and it is gone on purpose.

                It was the take's *level* — the one control, along with `Trim`,
                that fixed a recording rather than colouring it — and auto master
                now does that job and does it better: a peak normalise cannot hear
                the difference between a quiet room and a loud one, which is
                exactly the complaint ("this page is quieter than the last").
                Two buttons that both raise the volume, one of them roughly, is
                the confusion this desk was asked to remove. Nothing is lost that
                the take needed: the level is still set from a measurement, and
                the summary line still reports it.
              */}
            </View>
          </View>

          {/*
            The voice effects — the desk's whole manual half.

            This is the second thing on the desk, and the only thing a creator can
            change by hand. It used to be behind a `Fine-tune` row, under five
            technical dials (`Warmth`, `Clarity`, `Evenness`, `Room`, `Clean up`)
            and a row of fade lengths, and *that* was the problem: a creator
            recording a reading is choosing how it should sound, not operating a
            mixing console, and a knob called `Evenness` has to be explained before
            it can be used. The presets say what they do in words — *Warm*, *Clear*,
            *Storyteller* — and auto master is above them for the take that only
            needs to be right.

            Nothing was taken away from the engine: every preset still sets the
            depth, brightness, room, echo and pace it always did, and the line
            under the title still names what is applied, so nothing shapes the
            sound unseen.
          */}
          <Text style={styles.sectionLabel}>{l`VOICE EFFECTS`}</Text>
          <View style={styles.chipWrap}>
            {AUDIO_PRESETS.map(preset => {
              const active = activePreset === preset.id
              return (
                <TouchableOpacity
                  key={preset.id}
                  accessibilityRole="button"
                  /*
                   * `aria-selected` rather than `accessibilityState={{selected}}`.
                   *
                   * They are the same state to a device — React Native's types
                   * document `aria-selected` as an alias for it — but they are not
                   * the same state to a browser: react-native-web 0.21 no longer
                   * reads `accessibilityState` at all (only its `disabled`), so the
                   * desk was applying a voice without ever *saying* which one was
                   * applied to anyone reading it with assistive technology. The
                   * highlighted chip was the only witness, and a highlight is not
                   * an accessible name.
                   */
                  aria-selected={active}
                  accessibilityLabel={preset.label}
                  accessibilityHint={preset.intent}
                  onPress={() => applyPreset(preset.id)}
                  style={[
                    styles.chip,
                    active
                      ? {backgroundColor: accent, borderColor: accent}
                      : {borderColor: chrome.borderStrong},
                  ]}>
                  <Text
                    style={[
                      styles.chipTxt,
                      active ? {color: accentInk} : {color: chrome.textMuted},
                    ]}>
                    {preset.label}
                  </Text>
                </TouchableOpacity>
              )
            })}
          </View>
        </>
      )}
    </View>
  )
}

/** Whether the editor can run at all in this build — the studio checks this. */
export const AUDIO_EDITOR_AVAILABLE = canEditAudio()

const makeStyles = (chrome: StudioChrome) =>
  StyleSheet.create({
    panel: {
      borderWidth: 1,
      borderRadius: 16,
      padding: 12,
      gap: 10,
      backgroundColor: chrome.fill,
    },
    header: {flexDirection: 'row', alignItems: 'center', gap: 7},
    title: {fontSize: 10, fontWeight: '800', letterSpacing: 1.2},
    subtitle: {
      flex: 1,
      fontSize: 11,
      fontWeight: '600',
      color: chrome.textFaint,
    },
    iconBtn: {padding: 4},
    loading: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      paddingVertical: 14,
    },
    loadingTxt: {fontSize: 12, color: chrome.textMuted},
    notice: {
      borderWidth: 1,
      borderRadius: 12,
      paddingHorizontal: 10,
      paddingVertical: 9,
      gap: 9,
    },
    noticeRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: 8,
    },
    noticeTxt: {flex: 1, fontSize: 12, color: chrome.textMuted, lineHeight: 17},
    waveWrap: {
      borderRadius: 12,
      borderWidth: 1,
      borderColor: chrome.borderStrong,
      backgroundColor: chrome.stage,
      paddingTop: 6,
      paddingBottom: 6,
      paddingHorizontal: 6,
      gap: 4,
      overflow: 'hidden',
    },
    trimRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      paddingHorizontal: 2,
    },
    trimTxt: {
      flex: 1,
      fontSize: 11,
      fontWeight: '600',
      color: chrome.textFaint,
    },
    trimStrong: {fontWeight: '800', fontVariant: ['tabular-nums']},
    miniBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      alignSelf: 'flex-start',
      gap: 5,
      borderWidth: 1,
      borderRadius: 14,
      paddingHorizontal: 11,
      paddingVertical: 7,
    },
    miniTxt: {fontSize: 11, fontWeight: '700'},
    miniGhost: {padding: 8},
    /*
     * Auto master's card: a roundel, a title, one button, a sentence.
     *
     * Deliberately the only thing on the desk wearing an accent border once it
     * has run — a mastered take should look *done* — and deliberately the only
     * card at the top, because it is the question the desk exists to answer.
     */
    master: {
      borderWidth: 1,
      borderRadius: 14,
      paddingHorizontal: 11,
      paddingVertical: 10,
      gap: 7,
      backgroundColor: chrome.fill,
    },
    masterHead: {flexDirection: 'row', alignItems: 'center', gap: 8},
    masterIcon: {
      width: 22,
      height: 22,
      borderRadius: 11,
      alignItems: 'center',
      justifyContent: 'center',
    },
    masterTitle: {flex: 1, fontSize: 12.5, fontWeight: '800'},
    masterBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      borderRadius: 16,
      paddingHorizontal: 13,
      paddingVertical: 8,
      minWidth: 96,
    },
    masterBtnTxt: {fontSize: 12, fontWeight: '800'},
    masterTxt: {fontSize: 11.5, lineHeight: 17, color: chrome.textMuted},
    masterMeta: {
      fontSize: 11,
      fontWeight: '800',
      color: chrome.text,
      lineHeight: 16,
    },
    // Wraps rather than crushing: at a phone's 330px the three actions do not fit
    // one line, and Apply on a second line is still on screen — which is the
    // whole point of the bar.
    transport: {
      flexDirection: 'row',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 8,
    },
    transportSpacer: {flexGrow: 1, minWidth: 8},
    playBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      borderRadius: 20,
      paddingHorizontal: 14,
      paddingVertical: 10,
    },
    playTxt: {fontSize: 12.5, fontWeight: '800'},
    btn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      borderWidth: 1,
      borderRadius: 20,
      paddingHorizontal: 12,
      paddingVertical: 10,
      borderColor: 'transparent',
    },
    btnTxt: {fontSize: 12.5, fontWeight: '700'},
    sectionLabel: {
      fontSize: 9.5,
      fontWeight: '800',
      letterSpacing: 1.3,
      color: chrome.textFaint,
      marginTop: 2,
    },
    /*
     * Eight presets on a 330px card: at the old metrics they wrapped to four
     * rows and were the tallest thing on the desk. Tighter padding and type
     * bring the whole family onto two, without hiding any of them behind a
     * scroller — a horizontal `ScrollView` would set `touch-action: none` here
     * and swallow the vertical swipe that reaches the scrubber below.
     */
    chipWrap: {flexDirection: 'row', flexWrap: 'wrap', gap: 5},
    chip: {
      borderWidth: 1,
      borderRadius: 13,
      paddingHorizontal: 9,
      paddingVertical: 6,
      /*
       * `minHeight` rather than more padding: the label is 11px, so the chip's own
       * height came to 27px — a third of a millimetre under the point where a
       * thumb stops being sure. `minHeight` states the target itself, so a smaller
       * type scale or a different platform's line height cannot quietly take the
       * desk back under it.
       */
      minHeight: 32,
      alignItems: 'center',
      justifyContent: 'center',
    },
    chipTxt: {fontSize: 11, fontWeight: '700'},
  })

/** Styles for the appearance the app is in. */
const useStyles = () => useStudioStyles(makeStyles)
